<template>
  <div class="kpt-list-page">
    <vue-headful title="Subscriptions" />
    <header class="kpt-list-header">
      <h4>Subscriptions</h4>
      <p class="is-size-6">Count: {{ total }}</p>
    </header>
    <div class="is-flex p-3 is-align-items-center">
      <div class="buttons mr-2 my-0">
        <b-button size="is-small" icon-left="sync" @click="getSubscriptions" class="mb-0">
          Refresh List
        </b-button>
      </div>
      <b-input size="is-small" placeholder="Filter..." v-model="query" @keyup.native.enter="runQueryFilter" style="width: 200px"></b-input>
      <b-select size="is-small" placeholder="Filter by Account" class="mx-2" @input="(v) => toggleAccountFilter(v)">
        <option :value="null">All</option>
        <option
            v-for="account in accounts"
            :value="account.id"
            :key="account.id">
            {{ account.name }}
        </option>
      </b-select>
      <b-field>
        <b-datepicker
          size="is-small"
          placeholder="Select End Date range..."
          v-model="dates"
          @input="(v) => dateRangeTrigger(v)"
          range>
        </b-datepicker>
        <div class="control">
          <b-button icon-right="times" size="is-small" :disabled="!dates.length" @click="clearDateRange"></b-button>
        </div>
      </b-field>
    </div>
    <div class="kpt-list-table">
      <b-table
        :data="subscriptions"
         sticky-header
        :narrowed="true"
        :hoverable="true"
        :loading="isLoading"

        sort-icon="chevron-up"
        sort-icon-size="is-small"
        backend-sorting
        :default-sort="['EndDate', 'asc']"
        default-sort-direction="asc"
        @sort="onSort"

        paginated
        backend-pagination
        :per-page="pageSize"
        :total="total"
        @page-change="onPageChange"
        
        class="has-horizontal-scroll is-full-height">
        <b-table-column field="SubscriptionNumber" label="Sub Number" v-slot="props" width="125" sortable>
          {{ props.row.subscriptionNumber || 'Unknown' }}
        </b-table-column>
        <b-table-column field="Description" label="Product" v-slot="props" sortable>
          <router-link :to="{ name: 'subscription2Overview', params: { id: props.row.id } }">{{props.row.description || 'Unknown'}}</router-link>
        </b-table-column>
        <b-table-column field="AccountName" label="Account" v-slot="props">
          {{ getAccountNameFromId(props.row.accountId) }}
        </b-table-column>
        <b-table-column field="StartDate" label="Start Date" v-slot="props" width="200" sortable>
          {{ format(addMinutes(new Date(props.row.startDate), new Date(props.row.startDate).getTimezoneOffset()), 'yyyy-MM-dd') }}
        </b-table-column>
        <b-table-column field="EndDate" label="End Date" v-slot="props" width="200" sortable>
          {{ format(addMinutes(new Date(props.row.endDate), new Date(props.row.endDate).getTimezoneOffset()), 'yyyy-MM-dd') }}
        </b-table-column>
        <template slot="empty">
          <div class="p-6 is-flex is-justify-content-center">
            <p>No subscriptions were found.</p>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>
<script setup>
import { 
  onMounted, ref, computed, watch
} from 'vue';
import { format, addMinutes } from 'date-fns';
import { useSubscriptions2Store } from './subscriptions2-store';
import { useAccountsStore } from '../accounts/accounts-store';
import getAccountNameFromId from '../utilities/getAccountNameFromId';

const store = useSubscriptions2Store();
const accountsStore = useAccountsStore();
const subscriptions = computed(() => store.state.subscriptions);
const accounts = computed(() => accountsStore.state.accounts);
const total = computed(() => store.state.subscriptionsTotal);
const isLoading = computed(() => store.state.subscriptionsLoading);

const currentPage = ref(1);
const pageSize = ref(50);
const sortField = ref('EndDate');
const sortDirection = ref('ASC');
const query = ref('');
const filters = ref([]);
const dates = ref([]);

const getSubscriptions = async () => {
  if (!pageSize.value) pageSize.value = 50;
  await store.dispatch('fetchSubscriptions', { 
    skip: (currentPage.value - 1) * pageSize.value,
    take: pageSize.value, 
    sortField: sortField.value, 
    sortDirection: sortDirection.value,
    fields: 'Id,Description,StartDate,EndDate,AccountId,SubscriptionNumber',
    query: query.value,
    filters: filters.value.join(',')
  });
}

const onSort = async (field, order) => {
  sortField.value = field;
  sortDirection.value = order.toUpperCase();
  await getSubscriptions();
}
const onPageChange = async (page) => {
  currentPage.value = page;
  await getSubscriptions();
}
const toggleAccountFilter = async (value) => {
  filters.value = filters.value.filter(v => !v.includes('AccountId'));
  if (value) {
    filters.value.push(`AccountId:${value}`);
  }
}
const dateRangeTrigger = async (value) => {
  const rangeStart = value[0]?.getTime();
  const rangeEnd = value[1]?.getTime();
  if (rangeStart && rangeEnd) {
    filters.value.push(`EndDate:[${rangeStart} ${rangeEnd}]`);
    await getSubscriptions();
  }
}
const clearDateRange = async () => {
  dates.value = [];
  filters.value = filters.value.filter(v => !v.includes('EndDate'));
  await getSubscriptions();
}
watch(filters, async () => {
  await getSubscriptions();
})

const runQueryFilter = async () => {
  currentPage.value = 1;
  await getSubscriptions();
}

onMounted(async () => {
  await getSubscriptions()
});
</script>
